import DocumentConnection, {
  DocumentConnectionAuthMode,
  DocumentConnectionGrant,
  DocumentConnectionStatus,
  DocumentConnectionTestResult,
  ProviderContainer,
  ProviderItem,
  ProviderItemMetadata,
  ProviderItemPage,
  ProviderPageContent,
} from 'models/documentConnection';
import { DocumentProvider, DocumentProviderCapabilities, DocumentProviderType } from 'models/documentProvider';
import {
  DocumentConnectionDTO,
  DocumentConnectionGrantDTO,
  DocumentConnectionTestResultDTO,
  DocumentProviderCapabilitiesDTO,
  DocumentProviderDescriptorDTO,
  ProviderContainerDTO,
  ProviderItemDTO,
  ProviderItemMetadataDTO,
  ProviderItemPageDTO,
  ProviderPageContentDTO,
} from 'models/dto/documentProviderDTO';

export function mapFromDocumentProviderCapabilities(
  dto: DocumentProviderCapabilitiesDTO,
): DocumentProviderCapabilities {
  const output = new DocumentProviderCapabilities();

  output.browse = dto.browse === true;
  output.linkExisting = dto.linkExisting === true;
  output.upload = dto.upload === true;
  output.createFolder = dto.createFolder === true;
  output.download = dto.download === true;
  output.embedPreview = dto.embedPreview === true;
  output.officeOnlinePreview = dto.officeOnlinePreview === true;
  output.webLink = dto.webLink === true;
  output.thumbnail = dto.thumbnail === true;
  output.versions = dto.versions === true;
  output.publish = dto.publish === true;
  output.approvals = dto.approvals === true;
  output.readConfirmations = dto.readConfirmations === true;
  output.pagesKind = dto.pagesKind === true;
  output.pageEdit = dto.pageEdit === true;
  output.docxEdit = dto.docxEdit === true;
  output.providerSearch = dto.providerSearch === true;
  output.deleteContent = dto.deleteContent === true;
  output.customList = dto.customList === true;
  output.wholeList = dto.wholeList === true;
  output.writeProbe = dto.writeProbe === true;
  output.contentIngest = dto.contentIngest === true;
  output.requiresConnection = dto.requiresConnection === true;
  output.supportsDomainDelegation = dto.supportsDomainDelegation === true;
  output.supportsUserDelegation = dto.supportsUserDelegation === true;
  output.pdfConversion = dto.pdfConversion === true;

  return output;
}

export function mapFromDocumentProvider(dto: DocumentProviderDescriptorDTO): DocumentProvider {
  const output = new DocumentProvider();

  output.providerType = dto.providerType as DocumentProviderType;
  output.name = dto.name;
  output.capabilities = mapFromDocumentProviderCapabilities(dto.capabilities ?? new DocumentProviderCapabilitiesDTO());
  output.enabled = dto.enabled === true;

  return output;
}

export function mapFromDocumentProviders(dtos: DocumentProviderDescriptorDTO[]): DocumentProvider[] {
  return (dtos ?? []).map((dto) => mapFromDocumentProvider(dto));
}

export function mapFromDocumentConnection(dto: DocumentConnectionDTO): DocumentConnection {
  const output = new DocumentConnection();

  output.documentConnectionId = dto.documentConnectionId;
  output.providerType = dto.providerType as DocumentProviderType;
  output.name = dto.name;
  output.status = dto.status as DocumentConnectionStatus;
  // an API without the field is one that only knew the stored-credential mode
  output.authMode = (dto.authMode ?? DocumentConnectionAuthMode.TenantCredential) as DocumentConnectionAuthMode;
  output.isAuthorized = dto.isAuthorized;
  output.config = dto.config;
  output.hasAuthData = dto.hasAuthData === true;
  output.lastTestedDate = dto.lastTestedDate ? new Date(dto.lastTestedDate) : undefined;
  output.created = dto.created ? new Date(dto.created) : undefined;
  output.createdById = dto.createdById;

  return output;
}

export function mapFromDocumentConnections(dtos: DocumentConnectionDTO[]): DocumentConnection[] {
  return (dtos ?? []).map((dto) => mapFromDocumentConnection(dto));
}

export function mapToDocumentConnection(item: DocumentConnection): DocumentConnectionDTO {
  const output = new DocumentConnectionDTO();

  output.documentConnectionId = item.documentConnectionId;
  output.providerType = item.providerType;
  output.name = item.name;
  output.status = item.status;
  output.authMode = item.authMode;
  output.config = item.config;
  // an empty value means "keep the stored credentials"; the server never sent them here
  output.authData = item.authData ? item.authData : undefined;

  return output;
}

export function mapFromDocumentConnectionGrant(dto: DocumentConnectionGrantDTO): DocumentConnectionGrant {
  const output = new DocumentConnectionGrant();

  output.documentConnectionId = dto.documentConnectionId;
  output.connectionName = dto.connectionName;
  output.providerType = dto.providerType as DocumentProviderType;
  output.isAuthorized = dto.isAuthorized;
  output.lastTestedDate = dto.lastTestedDate ? new Date(dto.lastTestedDate) : undefined;
  output.created = dto.created ? new Date(dto.created) : undefined;

  return output;
}

export function mapFromDocumentConnectionGrants(dtos: DocumentConnectionGrantDTO[]): DocumentConnectionGrant[] {
  return (dtos ?? []).map((dto) => mapFromDocumentConnectionGrant(dto));
}

export function mapFromDocumentConnectionTestResult(
  dto: DocumentConnectionTestResultDTO,
): DocumentConnectionTestResult {
  const output = new DocumentConnectionTestResult();

  output.success = dto.success === true;
  output.message = dto.message;
  output.code = dto.code;
  output.accountDisplay = dto.accountDisplay;

  return output;
}

export function mapFromProviderContainer(dto: ProviderContainerDTO): ProviderContainer {
  const output = new ProviderContainer();

  output.id = dto.id;
  output.name = dto.name;
  output.kind = dto.kind;
  output.hasChildren = dto.hasChildren === true;
  // only an explicit false locks the picker, so an API without the flag keeps every container selectable
  output.canBeRoot = dto.canBeRoot !== false;

  return output;
}

export function mapFromProviderContainers(dtos: ProviderContainerDTO[]): ProviderContainer[] {
  return (dtos ?? []).map((dto) => mapFromProviderContainer(dto));
}

export function mapFromProviderItem(dto: ProviderItemDTO): ProviderItem {
  const output = new ProviderItem();

  output.id = dto.id;
  output.name = dto.name;
  output.browsePath = dto.browsePath;
  output.isFolder = dto.isFolder === true;
  output.mimeType = dto.mimeType;
  output.size = dto.size;
  output.modified = dto.modified ? new Date(dto.modified) : undefined;
  output.webUrl = dto.webUrl;
  output.canDownload = dto.canDownload === true;

  return output;
}

export function mapFromProviderItemPage(dto: ProviderItemPageDTO): ProviderItemPage {
  const output = new ProviderItemPage();

  output.items = (dto?.items ?? []).map((item) => mapFromProviderItem(item));
  output.nextPageToken = dto?.nextPageToken;

  return output;
}

export function mapFromProviderItemMetadata(dto: ProviderItemMetadataDTO): ProviderItemMetadata {
  const output = new ProviderItemMetadata();

  output.linkId = dto.linkId;
  output.itemId = dto.itemId;
  output.name = dto.name;
  output.mimeType = dto.mimeType;
  output.size = dto.size;
  output.lastModified = dto.lastModified ? new Date(dto.lastModified) : undefined;
  output.webUrl = dto.webUrl;
  output.hasError = dto.hasError === true;

  return output;
}

export function mapFromProviderItemMetadatas(dtos: ProviderItemMetadataDTO[]): ProviderItemMetadata[] {
  return (dtos ?? []).map((dto) => mapFromProviderItemMetadata(dto));
}

export function mapFromProviderPageContent(dto: ProviderPageContentDTO): ProviderPageContent {
  const output = new ProviderPageContent();

  output.title = dto?.title;
  output.markdown = dto?.markdown;
  output.html = dto?.html;
  output.lastModified = dto?.lastModified ? new Date(dto.lastModified) : undefined;
  output.webUrl = dto?.webUrl;

  return output;
}
